import { StatusCodes } from 'http-status-codes';
import i18next from 'i18next';

import store from 'app/store';

import { selectAuthToken } from 'features/auth/authSelectors';
import { logout } from 'features/auth/authSlice';

import { getTranslatedResponseMessage } from 'utils/api/getTranslatedResponseMessage';
import responseParser from 'utils/api/responseParser';
import { notify } from 'utils/toast/createToast';

import { ApiErrorResponse, ErrorResponse, ParserError } from 'types/api';

import {
  ACCOUNT_NOT_ACTIVATED_MESSAGE_TEXT,
  INVALID_TOKEN_MESSAGE_TEXT,
  NO_INTERNET_CONNECTION,
  SESSION_EXPIRED_MESSAGE_TEXT,
  UNKNOWN_ERROR
} from 'constants/notifications';
import { ACCESS_PENDING } from 'constants/path';
import { PATHS_PREVENTING_ACCESS_PENDING_REDIRECT } from 'constants/pathGroups';
import { ERROR_STATUS_CODES_FOR_LOGOUT } from 'constants/statusCodeGroups';

const MessageMap = {
  [StatusCodes.GONE]: i18next.t(SESSION_EXPIRED_MESSAGE_TEXT),
  [StatusCodes.UNAUTHORIZED]: i18next.t(INVALID_TOKEN_MESSAGE_TEXT),
  UnknownError: i18next.t(UNKNOWN_ERROR),
  NoInternetConnection: i18next.t(NO_INTERNET_CONNECTION)
};

const errorParser = async (error: ParserError) => {
  if (!navigator.onLine) {
    throw new Error(MessageMap.NoInternetConnection);
  }

  try {
    const response = error.response
      ? await responseParser(error.response)
      : null;

    const responseStatus = error.response?.status;

    const errorMessage = (response as ApiErrorResponse | null)?.message ?? null;

    const accountNotActivated =
      responseStatus === StatusCodes.FORBIDDEN &&
      errorMessage === ACCOUNT_NOT_ACTIVATED_MESSAGE_TEXT;

    if (
      accountNotActivated &&
      !PATHS_PREVENTING_ACCESS_PENDING_REDIRECT.includes(
        window.location.pathname
      )
    ) {
      window.location.href = ACCESS_PENDING;
      return;
    }

    if (
      responseStatus &&
      ERROR_STATUS_CODES_FOR_LOGOUT.includes(responseStatus)
    ) {
      const token = selectAuthToken(store.getState());
      // If token is not present, do not logout,
      // to not clear the entered email when login errors (e.g. wrong password)
      if (!token) {
        const errorDetails: ErrorResponse = {
          message: error.message || MessageMap.UnknownError,
          status: responseStatus,
          data: response
        };

        throw errorDetails;
      }
      notify({
        description: responseStatus
          ? MessageMap[responseStatus as keyof typeof MessageMap]
          : MessageMap.UnknownError
      });
      store.dispatch(logout());
    } else {
      const responseMessage = getTranslatedResponseMessage(
        response as ApiErrorResponse | null
      );

      const errorDetails: ErrorResponse = {
        message: responseMessage || error.message || MessageMap.UnknownError,
        status: responseStatus ?? StatusCodes.INTERNAL_SERVER_ERROR,
        data: response
      };

      console.error(errorDetails);
      throw errorDetails;
    }
  } catch (parseError) {
    console.error(parseError);
    throw parseError;
  }
};

export default errorParser;
